import { Link } from "react-router";
import { ChevronRight, X } from "lucide-react";
import type { PlateListing, PlatePosition } from "../../../api/catalogue";
import { describeQuantity } from "../../../lib/quantity";
import { AddToBasket, type AddToBasketState } from "../../cart/AddToBasket";
import { Badge, Icon } from "../../ui";
import { cx } from "../../ui/cx";
import { OriginBadge, PosChip, PriceTag, ReferenceNumber, StockBadge } from "../partLabels";

export interface PartPanelProps {
  position: PlatePosition;
  listing: PlateListing;
  onClose: () => void;
  onAdd: (quantity: number) => void;
  addState: AddToBasketState;
  /** Phone sizing, inside the bottom sheet. */
  touch?: boolean;
}

/** The part picked on the plate: what it is, its price, and the way to its page. */
export function PartPanel({ position, listing, onClose, onAdd, addState, touch = false }: PartPanelProps) {
  return (
    <div className={touch ? "px-5 pt-3 pb-7" : "p-5"}>
      <div className="flex items-start gap-4">
        <PosChip label={position.position_number} active size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ReferenceNumber listing={listing} className="w-auto" />
            <OriginBadge origin={listing.origin} />
          </div>
          <p className={cx("mt-1 mb-0 type-h4 text-text-strong", touch ? "[font-size:var(--text-md)]" : "[font-size:var(--text-base)]")}>{listing.name}</p>
          <p className="mt-0.5 mb-0 type-small text-text-muted">
            {[listing.dimension, `${describeQuantity(listing)} per assembly`, `SKU ${listing.sku}`].filter(Boolean).join(" · ")}
          </p>
          {position.position_note && <p className="mt-1 mb-0 type-small text-text-muted">{position.position_note}</p>}
          {listing.replaced_by && (
            <p className="mt-2 mb-0 type-small text-warning-700">
              Replaced by{" "}
              <Link to={`/listings/${listing.replaced_by.id}`} className="type-sku">
                {listing.replaced_by.sku}
              </Link>
            </p>
          )}
        </div>
        {!touch && <PriceTag cents={listing.price_cents} unit={listing.unit} className="flex-none" />}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cx("flex flex-none cursor-pointer items-center justify-center rounded-sm text-ink-600 hover:bg-paper-2", touch ? "-mt-2 -mr-3 size-12" : "size-8")}
        >
          <Icon icon={X} size={touch ? 22 : 18} />
        </button>
      </div>

      {touch && (
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-border-subtle pt-5">
          <PriceTag cents={listing.price_cents} unit={listing.unit} size="lg" />
          <StockBadge inStock={listing.in_stock} />
        </div>
      )}

      <div className={cx("flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-border-default", touch ? "mt-5 pt-5" : "mt-4 pt-4")}>
        {!touch && <StockBadge inStock={listing.in_stock} />}
        <AddToBasket listing={listing} onAdd={onAdd} state={addState} touch={touch} />
        <Link
          to={`/listings/${listing.id}`}
          className={cx("inline-flex items-center gap-1 border-b-0 type-label text-text-link", touch ? "min-h-11 w-full justify-center" : "ml-auto")}
        >
          Part details <Icon icon={ChevronRight} size={16} />
        </Link>
      </div>
    </div>
  );
}

export interface VersionChooserProps {
  position: PlatePosition;
  onPick: (listing: PlateListing) => void;
  onClose: () => void;
  touch?: boolean;
}

/** Several parts at one position, like one crankshaft per engine version: the customer picks theirs. */
export function VersionChooser({ position, onPick, onClose, touch = false }: VersionChooserProps) {
  const count = position.listings.length;
  return (
    <div className={touch ? "px-5 pt-3 pb-7" : "p-5"}>
      <div className="flex items-start gap-4">
        <PosChip label={position.position_number} active size="lg" />
        <div className="min-w-0 flex-1">
          <p className="mb-0 type-h4 [font-size:var(--text-base)] text-text-strong">{count} parts at this position</p>
          <p className="mt-0.5 mb-0 type-small text-text-muted">
            They're versions of the same part for different engines. Pick the one that matches yours; the description says which.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cx("flex flex-none cursor-pointer items-center justify-center rounded-sm text-ink-600 hover:bg-paper-2", touch ? "-mt-2 -mr-3 size-12" : "size-8")}
        >
          <Icon icon={X} size={touch ? 22 : 18} />
        </button>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        {position.listings.map((listing) => (
          <button
            key={listing.id}
            type="button"
            onClick={() => onPick(listing)}
            className={cx(
              "flex w-full cursor-pointer items-center gap-4 rounded-sm border border-border-subtle bg-surface-card px-4 text-left hover:border-green-700",
              touch ? "min-h-(--control-touch) py-3" : "py-3",
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block type-h4 [font-size:var(--text-sm)] text-text-strong">{listing.name}</span>
              <span className="mt-0.5 block type-sku [font-size:var(--text-xs)] text-ink-600">{listing.reference_number ?? listing.sku}</span>
            </span>
            <PriceTag cents={listing.price_cents} unit={listing.unit} size="sm" className="flex-none" />
            {!listing.in_stock && <Badge tone="out">Out</Badge>}
            <Icon icon={ChevronRight} size={16} className="text-ink-400" />
          </button>
        ))}
      </div>
    </div>
  );
}
